import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useToastNotifications } from "../../../features/toast-notification/toastNotificationSlice.js";
import {
  upsertUserAsync,
  clearError,
  quickCreateAccessTagAsync,
  readAllAccessTagsAsync
} from "../../../features/user/userSlice.js";
import { ExclamationCircleIcon } from "@heroicons/react/solid";
import { DocumentReportIcon } from "@heroicons/react/outline";
import { useGroupOptions } from "../../../features/group/groupSlice.js";
import Multiselect from "../../general/Multiselect.js";
import ImageUploaderFormField from "../utils/ImageUploaderFormField.js";
import QuotaFormFields from "../utils/QuotaFormFields.js";
import axios from "axios";
import { Label } from "../utils/Common.js";
import Button from "../../misc/Button.js";
import { areDashboardQuotasEnabled } from "../../../feature-flags/prodFeatureFlags.js";
import { apiErrorMessage } from "../../../utils";

// additionalInfo is a shared JSON bag (profileLayout, legacyNumbers, ...) that may also
// hold legacy free text
const getContactEmail = additionalInfo => {
  try {
    const parsed = typeof additionalInfo === "string" ? JSON.parse(additionalInfo) : additionalInfo;
    return parsed?.contact?.email || "";
  } catch (e) {
    return "";
  }
};

const UserForm = ({ model, closeModal, handleSubmitted, title }) => {
  const { currentUser } = useSelector(state => state.auth);
  const { platformData } = useSelector(state => state.frontend);
  const { flashSuccessNotification, flashErrorNotification } = useToastNotifications();
  const [state, setState] = useState({
    ...model,
    contactEmail: getContactEmail(model?.additionalInfo),
    PlatformId: currentUser.PlatformId,
    GroupIds: (model?.Groups || []).reduce((acc, group) => {
      acc.push(group.id);
      return acc;
    }, []),
    AccessTagIds: (model?.AccessTags || []).reduce((acc, tag) => {
      acc.push(tag.id);
      return acc;
    }, []),
    quotas:
      model?.quotas ||
      (!model?.id && platformData.settings?.userRestrictedLiveStreaming === true
        ? { liveStreamingRestricted: true }
        : {})
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);

  const { error, generalError, accessTags } = useSelector(state => state.user);

  const groupOptions = useGroupOptions();

  const isCreator = currentUser.role === "creator";
  const isAdmin = currentUser.role === "admin";
  const isEditingOwnProfile = state.id === currentUser.id;
  const isEditingCreator = state.role === "creator";
  const isEditingCreatorAndAbove = state.role === "creator" || state.role === "admin";
  const shouldShowContactEmail =
    (isEditingOwnProfile && isCreator) || (isAdmin && isEditingCreator);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(readAllAccessTagsAsync());
  }, [dispatch, currentUser.PlatformId]);

  const onChange = event => {
    const { name, value } = event.target;

    if (error && error[name]) {
      dispatch(clearError({ field: name }));
    }

    setState(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const onQuotaChange = quotasValue => {
    setState(prevState => ({
      ...prevState,
      quotas: quotasValue
    }));
  };

  const onSubmit = async event => {
    event.preventDefault();
    if (isSubmitting) return;
    const user = {
      ...state,
      role: state.role || "free",
      PlatformId: currentUser.PlatformId
    };
    delete user.Groups;
    delete user.contactEmail;
    if (shouldShowContactEmail) {
      user.contactEmail = (state.contactEmail || "").trim();
    }
    setIsSubmitting(true);
    try {
      const action = await dispatch(upsertUserAsync({ user }));
      if (action.error) {
        flashErrorNotification(action.payload?.message);
        return;
      }
      if (handleSubmitted) handleSubmitted();
      flashSuccessNotification("User saved successfully");
      closeModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  const onForgotPassword = async () => {
    if (isSendingPasswordReset) return;
    setIsSendingPasswordReset(true);
    try {
      await axios.post(`/auth/forgot-password`, {
        email: state.email,
        PlatformId: state.PlatformId,
        windowLocation: `${window.location.protocol}//${window.location.host}/`
      });
      flashSuccessNotification("Password reset email sent");
    } catch (err) {
      const status = err.response?.status;
      flashErrorNotification(status === 404 ? "User not found" : apiErrorMessage(err));
    } finally {
      setIsSendingPasswordReset(false);
    }
  };

  const onClearPassword = async () => {
    if (
      !window.confirm("Clear this user's password? They will need to set a new one on next login.")
    ) {
      return;
    }
    try {
      await axios.delete(`/api/user/${state.id}/clear-password`);
      flashSuccessNotification("Password cleared - user will set new password on next login");
    } catch (err) {
      flashErrorNotification(apiErrorMessage(err, "Failed to clear password"));
    }
  };

  const isTagLoading = useSelector(state => state.user.pendingAccessTagTitles?.length > 0);
  const tagsOptions = accessTags?.map(tag => ({ id: tag.id, label: tag.title }));
  const tagQuickCreateHandler = title =>
    dispatch(quickCreateAccessTagAsync({ title, PlatformId: state.PlatformId }));

  return (
    <form onSubmit={event => onSubmit(event)} id="user-form">
      {title && (
        <div className="flex flex-row justify-between items-center mb-6">
          <h6 className="text-2xl leading-9 font-bold text-gray-900">{title}</h6>
          {isAdmin && state.id && (
            <Link
              to={`/dashboard/reports/user-activity?userId=${state.id}`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out"
              id="view-activity-log-link"
            >
              <DocumentReportIcon className="h-4 w-4 mr-2" />
              View Activity Log
            </Link>
          )}
        </div>
      )}

      {state.id && (
        <div className="mb-4 space-y-1 italic opacity-30">
          <div>User ID: {state.id}</div>
        </div>
      )}

      <div>
        <div className="flex flex-col sm:flex-row justify-between">
          <div className="w-full sm:w-1/2">
            {generalError && (
              <p className="text-sm leading-5 font-medium text-red-800 mb-1">{generalError}</p>
            )}
            <div id="name-input-div" className="mt-4">
              <Label htmlFor="name">Name</Label>
              <div className="mt-1 rounded-md text-gray-800 shadow-sm relative">
                <input
                  required
                  id="name"
                  autoFocus
                  name="name"
                  className={`${
                    error && error.name
                      ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                      : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                  } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                  value={(state && state.name) || ""}
                  onChange={event => onChange(event)}
                  placeholder="Name"
                />
                {error && error.name && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="w-5 text-red-600" />
                  </div>
                )}
              </div>
              {error && error.name && <p className="mt-2 text-sm text-red-600">{error.name}</p>}
            </div>
            <div className="mt-6" id="email-input-div">
              <Label htmlFor="email">Email</Label>
              <div className="mt-1 rounded-md  text-gray-800 shadow-sm relative">
                <input
                  id="email"
                  name="email"
                  className={`${
                    error && error.email
                      ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                      : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                  } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                  value={(state && state.email) || ""}
                  onChange={event => onChange(event)}
                  placeholder="email@test.com"
                />
                {error && error.email && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="w-5 text-red-600" />
                  </div>
                )}
              </div>
              {error && error.email && <p className="mt-2 text-sm text-red-600">{error.email}</p>}
            </div>

            {state.id ? (
              <>
                <button
                  className="mt-8 w-full inline-flex justify-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-slate-700 bg-white hover:text-slate-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                  onClick={onForgotPassword}
                  disabled={isSendingPasswordReset}
                  id="send-password-reset-email-button"
                >
                  Send password reset email
                </button>
                {isAdmin && !isEditingOwnProfile && (
                  <button
                    className="mt-4 w-full inline-flex justify-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-slate-700 bg-white hover:text-slate-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 transition duration-150 ease-in-out"
                    type="button"
                    onClick={onClearPassword}
                    id="clear-password-button"
                  >
                    Clear password
                  </button>
                )}
              </>
            ) : (
              <div className="mt-6" id="password-input-div">
                <Label htmlFor="password">Password</Label>
                <div className="mt-1 text-gray-800 rounded-md shadow-sm relative">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    className={`${
                      error && error.password
                        ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                        : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                    } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                    value={(state && state.password) || ""}
                    onChange={event => onChange(event)}
                    placeholder="password"
                  />
                  {error && error.password && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon className="w-5 text-red-600" />
                    </div>
                  )}
                </div>
                {error && error.password && (
                  <p className="mt-2 text-sm text-red-600">{error.password}</p>
                )}
              </div>
            )}
            {currentUser.role === "admin" && (
              <div className="mt-6" id="stripe-id-input-div">
                <Label htmlFor="stripeCustomerId">Stripe Customer ID</Label>
                <div className="mt-1 text-gray-800 rounded-md shadow-sm relative">
                  <input
                    id="stripeCustomerId"
                    name="stripeCustomerId"
                    type="text"
                    className={`${
                      error && error.stripeCustomerId
                        ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                        : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                    } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                    value={(state && state.stripeCustomerId) || ""}
                    onChange={event => onChange(event)}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="w-full sm:w-1/2 sm:pl-6">
            <div className="mt-4 sm:mt-9 rounded-md shadow-sm relative" id="avatar-uploader-div">
              <ImageUploaderFormField
                title="Avatar"
                field="photoUrl"
                description="SVG, PNG, JPG, GIF up to 10MB"
                value={state.photoUrl}
                onChange={value => onChange({ target: { name: "photoUrl", value } })}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-8 mt-4">
          <div id="city-input-div">
            <Label htmlFor="city">City</Label>
            <div className="mt-1 rounded-md text-gray-800 shadow-sm relative">
              <input
                id="city"
                name="city"
                className={`${
                  error && error.city
                    ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                    : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                value={(state && state.city) || ""}
                onChange={event => onChange(event)}
                placeholder="City"
              />
              {error && error.city && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="w-5 text-red-600" />
                </div>
              )}
            </div>
            {error && error.city && <p className="mt-2 text-sm text-red-600">{error.city}</p>}
          </div>
          <div id="state-input-div">
            <Label htmlFor="state">State</Label>
            <div className="mt-1 rounded-md text-gray-800 shadow-sm relative">
              <input
                id="state"
                name="state"
                className={`${
                  error && error.state
                    ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                    : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                value={(state && state.state) || ""}
                onChange={event => onChange(event)}
                placeholder="State"
              />
              {error && error.state && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="w-5 text-red-600" />
                </div>
              )}
            </div>
            {error && error.state && <p className="mt-2 text-sm text-red-600">{error.state}</p>}
          </div>
        </div>
        <div className="mt-6" id="about-input-div">
          <Label htmlFor="notes">About / Description</Label>
          <div className="mt-1 text-gray-800 rounded-md shadow-sm relative">
            <textarea
              id="notes"
              name="notes"
              type="textarea"
              className={`${
                error && error.notes
                  ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                  : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
              } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
              value={(state && state.notes) || ""}
              onChange={event => onChange(event)}
              placeholder="Tell us about yourself..."
            />
            {error && error.notes && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <ExclamationCircleIcon className="w-5 text-red-600" />
              </div>
            )}
          </div>
          {error && error.notes && <p className="mt-2 text-sm text-red-600">{error.notes}</p>}
        </div>
        {shouldShowContactEmail && (
          <div className="mt-6" id="contact-email-input-div">
            <Label htmlFor="contactEmail">Public contact email</Label>
            <p className="mt-1 text-sm text-gray-500">
              Shown to everyone on the public creator profile. Leave blank to hide it.
            </p>
            <div className="mt-1 rounded-md text-gray-800 shadow-sm relative">
              <input
                id="contactEmail"
                name="contactEmail"
                className={`${
                  error && error.contactEmail
                    ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                    : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                value={(state && state.contactEmail) || ""}
                onChange={event => onChange(event)}
                placeholder="hello@example.org"
              />
              {error && error.contactEmail && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="w-5 text-red-600" />
                </div>
              )}
            </div>
            {error && error.contactEmail && (
              <p className="mt-2 text-sm text-red-600">{error.contactEmail}</p>
            )}
          </div>
        )}
        {isAdmin && (
          <>
            <div className="mt-6 text-gray-800" id="groups-select-div">
              <Label htmlFor="GroupIds">Groups</Label>
              <Multiselect
                id="GroupIds"
                name="GroupIds"
                value={(state && state.GroupIds) || []}
                onChange={onChange}
                multiple
                options={groupOptions}
              />
            </div>
            <div className="mt-6 text-gray-800" id="tags-select-div">
              <Label htmlFor="AccessTagIds">Tags</Label>
              <Multiselect
                id="AccessTagIds"
                name="AccessTagIds"
                options={tagsOptions}
                value={state.AccessTagIds || []}
                onChange={onChange}
                placeholder="Choose or create tags"
                searchPlaceholder="Search or create"
                customActionText="Create new tag"
                customActionHandler={tagQuickCreateHandler}
                isCustomActionLoading={isTagLoading}
              />
            </div>
            <div className="mt-6" id="additional-info-input-div">
              <Label htmlFor="additionalInfo">Additional Info (Users will not see this)</Label>
              <div className="mt-1 text-gray-800 rounded-md shadow-sm relative">
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  type="textarea"
                  className={`${
                    error && error.additionalInfo
                      ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                      : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                  } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                  value={(state && state.additionalInfo) || ""}
                  onChange={event => onChange(event)}
                  placeholder="Tell us about yourself..."
                />
                {error && error.additionalInfo && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="w-5 text-red-600" />
                  </div>
                )}
              </div>
              {error && error.additionalInfo && (
                <p className="mt-2 text-sm text-red-600">{error.additionalInfo}</p>
              )}
            </div>
            {isEditingCreatorAndAbove && (
              <div className="mt-6" id="custom-analytics-url-input-div">
                <Label htmlFor="customAnalyticsUrl">Custom Analytics URL</Label>
                <div className="mt-1 text-gray-800 rounded-md shadow-sm relative">
                  <input
                    id="customAnalyticsUrl"
                    name="customAnalyticsUrl"
                    type="textarea"
                    className={`block w-full px-3 py-2 border rounded-md border-gray-300 focus:shadow-outline-blue focus:border-blue-300 focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                    value={(state && state.customAnalyticsUrl) || ""}
                    onChange={event => onChange(event)}
                  />
                </div>
              </div>
            )}
            <div className="mt-6 hidden" id="user-position-input-div">
              <Label htmlFor="position">Position (Users will not see this)</Label>
              <div className="mt-1 text-gray-800 rounded-md shadow-sm relative">
                <input
                  id="position"
                  name="position"
                  type="number"
                  className={`${
                    error && error.position
                      ? "border-red-300 text-red-300 focus:border-red-300 focus:shadow-outline-red"
                      : "border-gray-300 focus:shadow-outline-blue focus:border-blue-300"
                  } block w-full px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
                  value={(state && state.position) || ""}
                  onChange={event => onChange(event)}
                />
                {error && error.position && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="w-5 text-red-600" />
                  </div>
                )}
              </div>
              {error && error.position && (
                <p className="mt-2 text-sm text-red-600">{error.position}</p>
              )}
            </div>
          </>
        )}

        {isAdmin && !isEditingOwnProfile && (
          <div className="mt-6" id="role-select-div">
            <Label htmlFor="role">Role</Label>
            <div className="mt-1 rounded-md shadow-sm relative">
              <select
                id="role"
                className="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 border border-gray-300"
                name="role"
                value={(state && state.role) || ""}
                onChange={event => onChange(event)}
              >
                <option value="free">Free</option>
                <option value="basic">Basic</option>
                <option value="premium">Premium</option>
                <option value="creator">Creator</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {error && error.role && <p className="mt-2 text-sm text-red-600">{error.role}</p>}
          </div>
        )}

        {isAdmin && !isEditingOwnProfile && (
          <div className="mt-6" id="banned-checkbox-div">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="banned"
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-slate-700 transition duration-150 ease-in-out"
                  name="banned"
                  checked={(state && state.banned) || false}
                  onChange={() => {
                    setState(prevState => ({
                      ...prevState,
                      banned: !state.banned
                    }));
                  }}
                />
              </div>
              <Label htmlFor="banned" checkbox>
                Banned
              </Label>
            </div>
          </div>
        )}

        <div className="mt-6" id="cover-photo-uploader-div">
          <div className="mt-1 rounded-md shadow-sm relative">
            <ImageUploaderFormField
              title="Upload / Change your Cover Photo"
              field="bannerImageUrl"
              description="SVG, PNG, JPG, GIF up to 10MB"
              value={state.bannerImageUrl}
              instructions={"For best results, upload an image that is 1230px x 300px "}
              onChange={value => onChange({ target: { name: "bannerImageUrl", value } })}
            />
          </div>
        </div>

        {areDashboardQuotasEnabled && isAdmin && isEditingCreator && (
          <div className="mt-6" id="quota-fields-div">
            <QuotaFormFields
              value={state.quotas}
              onValueChange={onQuotaChange}
              error={error?.quotas}
              showLiveStreamingRestriction={
                platformData.settings?.userRestrictedLiveStreaming === true
              }
            />
          </div>
        )}
      </div>

      <div className="mt-8 grid grid-cols-2 gap-3">
        <Button onClick={closeModal} variant="secondary">
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          Save
        </Button>
      </div>
    </form>
  );
};

export default UserForm;
